import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import type { ReactNode } from 'react';

import { api, TOKEN_STORAGE_KEY, refreshAccessToken, type ApiError } from '../lib/api';

export interface AuthUser {
  id: string;
  email: string;
  role: 'super_admin' | 'agency_admin' | 'client';
  tenantId: string;
  firstName: string | null;
  lastName: string | null;
}

const USER_STORAGE_KEY = 'sb_user';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<AuthUser>;
  registerAgency: (payload: RegisterAgencyPayload) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

export interface RegisterAgencyPayload {
  email: string;
  password: string;
  agencyName: string;
  slug: string;
  planTier: 'solo' | 'pro' | 'enterprise';
  firstName: string;
  lastName: string;
  phone?: string;
}

export interface AuthResponse {
  accessToken: string;
  user: AuthUser;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true); // ← starts TRUE — we hydrate on mount
  const [error, setError] = useState<string | null>(null);

  // ── Hydrate on mount: try to refresh any existing session ──────────
  useEffect(() => {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    if (!stored) {
      setLoading(false);
      return;
    }

    // We have a stored user — try to refresh the access token.
    refreshAccessToken()
      .then((ok) => {
        if (ok) {
          // refreshAccessToken already wrote the new user to localStorage
          // and fired 'auth:refresh'. Read the updated user.
          const updated = localStorage.getItem(USER_STORAGE_KEY);
          if (updated) {
            try {
              setUser(JSON.parse(updated));
            } catch {
              // ignore
            }
          }
        } else {
          // Refresh failed — clear stale data.
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          localStorage.removeItem(USER_STORAGE_KEY);
        }
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // ── Listen for token refresh events from the axios interceptor ─────
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as AuthUser;
      setUser(detail);
    };
    window.addEventListener('auth:refresh', handler as EventListener);
    return () => window.removeEventListener('auth:refresh', handler as EventListener);
  }, []);

  // ── Persist helper ────────────────────────────────────────────────
  const persist = useCallback((resp: AuthResponse) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, resp.accessToken);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(resp.user));
    setUser(resp.user);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string): Promise<AuthUser> => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.post<AuthResponse>('/auth/login', { email, password });
        persist(r.data);
        return r.data.user;
      } catch (e) {
        const err = e as ApiError;
        setError(err.message ?? 'Login failed.');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [persist],
  );

  const registerAgency = useCallback(
    async (payload: RegisterAgencyPayload): Promise<AuthUser> => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.post<AuthResponse>('/auth/register-agency', payload);
        persist(r.data);
        return r.data.user;
      } catch (e) {
        const err = e as ApiError;
        setError(err.message ?? 'Registration failed.');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [persist],
  );

  const signOut = useCallback(async () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    setUser(null);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, error, signIn, registerAgency, signOut, clearError }),
    [user, loading, error, signIn, registerAgency, signOut, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}